import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import * as THREE from 'three'
import { bakeSettingsStore } from '../lib/bakeSettingsStore'
import { getSplatBounds, lightProbePositions, reflectionPositions } from '../lib/bakeLayout'

/**
 * BakePreviewGizmos — in-Canvas markers showing where light probes (cyan) and
 * reflection captures (orange) would land for the current Bake Lighting settings
 * (ENG-561). Mounted in the scene; renders only while the settings panel is open
 * and an environment is loaded. Recomputes when the user changes a parameter.
 *
 * Markers draw on top (depthTest off) so they read through the splat, like
 * Unreal's probe-placement gizmos.
 *
 * Perf: a dense grid (small spacing over a big splat) is easily thousands of
 * markers. We draw each group as a SINGLE InstancedMesh — one draw call per
 * group regardless of count — instead of one <mesh> per marker (which was
 * thousands of draw calls + React nodes, and froze the viewport).
 */

const _m4 = new THREE.Matrix4()

// Cap how many markers we actually draw. A dense grid over a large environment
// can be thousands of markers; rendering them all (transparent, depth-test off)
// tanks the framerate for no extra insight. We evenly subsample for display —
// the panel's count label still reports the true probe/capture total.
const MAX_MARKERS = 500
function subsample(arr) {
  if (arr.length <= MAX_MARKERS) return arr
  const stride = Math.ceil(arr.length / MAX_MARKERS)
  const out = []
  for (let i = 0; i < arr.length; i += stride) out.push(arr[i])
  return out
}

function InstancedMarkers({ positions, color, radius }) {
  const ref = useRef(null)
  const geom = useMemo(() => new THREE.SphereGeometry(radius, 8, 8), [radius])
  const mat = useMemo(
    () => new THREE.MeshBasicMaterial({ color, depthTest: false, transparent: true, opacity: 0.9 }),
    [color],
  )
  useEffect(() => () => { geom.dispose(); mat.dispose() }, [geom, mat])

  // Write instance transforms whenever the position set changes. The
  // instancedMesh is keyed by count below, so its buffer is always sized right.
  useLayoutEffect(() => {
    const mesh = ref.current
    if (!mesh) return
    for (let i = 0; i < positions.length; i++) {
      const p = positions[i]
      _m4.makeTranslation(p[0], p[1], p[2])
      mesh.setMatrixAt(i, _m4)
    }
    mesh.count = positions.length
    mesh.instanceMatrix.needsUpdate = true
  }, [positions])

  if (!positions.length) return null
  return (
    // key on length so a changed count remounts with a correctly-sized buffer.
    <instancedMesh
      key={positions.length}
      ref={ref}
      args={[geom, mat, positions.length]}
      renderOrder={999}
      frustumCulled={false}
    />
  )
}

export default function BakePreviewGizmos() {
  const [, force] = useState(0)
  useEffect(() => bakeSettingsStore.subscribe(() => force(n => n + 1)), [])
  const { open, params, show } = bakeSettingsStore.getState()

  // Compute bounds once per panel-open (getBoundingBox walks ~2M gaussians).
  const bounds = useMemo(() => (open ? getSplatBounds() : null), [open])
  const lp = useMemo(
    () => (bounds && show.lightProbes ? subsample(lightProbePositions(bounds, params.lpSpacing, params.lpLayers)) : []),
    [open, show.lightProbes, params.lpSpacing, params.lpLayers, bounds?.floorY, bounds?.min?.[0], bounds?.max?.[0]],
  )
  const refl = useMemo(
    () => (bounds && show.reflections ? subsample(reflectionPositions(bounds, params.reflSpacing, params.reflInset)) : []),
    [open, show.reflections, params.reflSpacing, params.reflInset, bounds?.floorY, bounds?.min?.[0], bounds?.max?.[0]],
  )

  if (!open) return null
  return (
    <group>
      <InstancedMarkers positions={lp} color="#22d3ee" radius={0.06} />
      <InstancedMarkers positions={refl} color="#fb923c" radius={0.11} />
    </group>
  )
}
